// Types for the AI (LLM) assistance surface — workspace on/off settings, monthly usage roll-up, and
// document summary generation. Mirrors the platform AiController / AiDtos JSON contract.

/** Per-workspace AI settings. `enabled` is opt-in (default off); a null cap means unlimited. */
export interface WorkspaceAiSettings {
  workspaceId: string;
  enabled: boolean;
  docSummaryEnabled: boolean;
  model: string | null;
  monthlyTokenCap: number | null;
  updatedAt: string;
}

export interface UpdateAiSettingsRequest {
  enabled?: boolean;
  docSummaryEnabled?: boolean;
  model?: string | null;
  monthlyTokenCap?: number | null;
}

export interface AiUsagePerModel {
  model: string;
  promptTokens: number;
  completionTokens: number;
  costMicros: number;
}

/** A workspace's AI usage for one YYYY-MM period. `costMicros` is USD millionths. */
export interface AiUsage {
  period: string;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalCostMicros: number;
  byModel: AiUsagePerModel[];
}

/** The result of generating a document summary — always a DRAFT for human review. */
export interface AiSummaryResult {
  summary: string;
  keyPoints: string[];
  model: string;
  promptTokens: number;
  completionTokens: number;
  costMicros: number;
}

/** The result of a translation — `cached` is true when served from the DB cache (no LLM call). */
export interface AiTranslation {
  translatedText: string;
  targetLang: string;
  model: string;
  cached: boolean;
}

/** USD millionths → a short "$0.0023" string. */
export function formatCostMicros(micros: number): string {
  const usd = micros / 1_000_000;
  if (usd === 0) return "$0";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

/** Compact token count: 12345 → "12.3K", 1200000 → "1.2M". */
export function formatTokens(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}
